export const printHandout = () => {
  const printWindow = window.open('', '_blank', 'width=900,height=800');
  
  const content = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>DEA Educational Handout</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; line-height: 1.6; max-width: 800px; margin: 0 auto; }
        h1 { color: #2c3e50; border-bottom: 3px solid #3498db; padding-bottom: 10px; }
        h2 { color: #2980b9; margin-top: 30px; border-bottom: 1px solid #eee; padding-bottom: 5px; }
        h3 { color: #16a085; margin-top: 20px; margin-bottom: 5px; }
        p { margin-bottom: 15px; }
        ul { margin-bottom: 15px; }
        li { margin-bottom: 5px; }
        
        /* Table Styles */
        table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 0.95rem; }
        th, td { border: 1px solid #ddd; padding: 12px; text-align: left; vertical-align: top; }
        th { background-color: #f2f2f2; font-weight: bold; color: #2c3e50; }
        tr:nth-child(even) { background-color: #fafafa; }

        /* Box Styles */
        .box { background: #f8f9fa; border-left: 5px solid #3498db; padding: 15px; margin: 20px 0; border-radius: 4px; }
        .formula { background: #eee; padding: 10px; font-family: 'Courier New', monospace; text-align: center; font-weight: bold; border-radius: 4px; }
        
        /* Print specifics */
        @media print {
          body { padding: 0; font-size: 12pt; }
          .no-print { display: none; }
          a { text-decoration: none; color: #333; }
        }
      </style>
    </head>
    <body>
      <div style="text-align: right;" class="no-print">
        <button onclick="window.print()" style="padding: 10px 20px; background: #3498db; color: white; border: none; cursor: pointer; font-size: 1rem; border-radius: 4px;">🖨️ Print / Save as PDF</button>
      </div>

      <h1>Understanding Data Envelopment Analysis (DEA)</h1>
      <p><strong>A Quick Reference Guide for Students</strong></p>

      <h2>1. The Core Concept</h2>
      <p><strong>DEA</strong> is a method used to evaluate performance. Unlike standard regression (which looks at the <em>average</em>), DEA looks at the <em>best</em>.</p>
      <ul>
        <li><strong>Frontier:</strong> The imaginary curve connecting the "best-in-class" units.</li>
        <li><strong>Efficiency:</strong> A score between 0 and 1. If you are on the frontier, your score is <strong>1 (100%)</strong>. If you are inside, your score is lower.</li>
      </ul>

      <h2>2. Key Decisions: Orientation</h2>
      <table class="table">
        <tr>
          <th width="30%">Orientation</th>
          <th>The Goal</th>
          <th>Real World Example</th>
        </tr>
        <tr>
          <td><strong>Input-Oriented</strong></td>
          <td>Minimize inputs while holding outputs fixed.</td>
          <td><em>"We need to cut costs (inputs) but keep serving the same number of customers (outputs)."</em></td>
        </tr>
        <tr>
          <td><strong>Output-Oriented</strong></td>
          <td>Maximize outputs while holding inputs fixed.</td>
          <td><em>"We can't hire more people (inputs), so we need to produce more widgets (outputs) with what we have."</em></td>
        </tr>
      </table>

      <h2>3. Returns to Scale (The Model Type)</h2>
      <div class="box">
        <strong>The Big Question:</strong> Does being bigger make you more efficient?
      </div>
      
      <h3>CCR Model (Constant Returns to Scale)</h3>
      <ul>
        <li>Assumes size doesn't matter.</li>
        <li>A small shop and a giant factory are compared directly.</li>
        <li><strong>Harder to achieve:</strong> You must be efficient globally.</li>
      </ul>

      <h3>BCC Model (Variable Returns to Scale)</h3>
      <ul>
        <li>Assumes size <em>does</em> matter.</li>
        <li>Small units are compared to small units; large to large.</li>
        <li><strong>Convexity Constraint:</strong> The BCC model adds a math rule (∑&lambda; = 1) ensuring you are only compared to a mix of peers similar to your size.</li>
        <li><strong>Result:</strong> Scores are usually higher in BCC because the comparison is "fairer."</li>
      </ul>

      <h2>4. Scale Efficiency (SE)</h2>
      <p>This tells us if a unit is the "wrong size."</p>
      <div class="formula">
        Scale Efficiency = CCR Efficiency &div; BCC Efficiency
      </div>
      <br/>
      <table>
        <tr>
          <th>Result</th>
          <th>Diagnosis</th>
          <th>Prescription</th>
        </tr>
        <tr>
          <td><strong>SE = 1</strong></td>
          <td>Optimal Scale</td>
          <td>Maintain current size.</td>
        </tr>
        <tr>
          <td><strong>SE < 1 (IRS)</strong></td>
          <td>Increasing Returns (Too Small)</td>
          <td>Grow larger to gain efficiency.</td>
        </tr>
        <tr>
          <td><strong>SE < 1 (DRS)</strong></td>
          <td>Decreasing Returns (Too Big)</td>
          <td>Shrink down to reduce complexity/bloat.</td>
        </tr>
      </table>

      <h2>5. Interpreting Your Results</h2>
      <ul>
        <li><strong>Peers (Benchmarks):</strong> The specific efficient units you should copy. If <em>Store A</em> is inefficient, and the results say "Peer: Store B", <em>Store A</em> should call <em>Store B</em> to ask for advice.</li>
        <li><strong>Targets:</strong> The exact numbers you need to hit to become efficient.</li>
        <li><strong>Slacks:</strong> Pure waste. Resources that could be dropped immediately without affecting output.</li>
      </ul>

      <div style="margin-top: 50px; border-top: 1px dashed #ccc; padding-top: 10px; color: #777;">
        <small>Generated by DEA Teaching Tool</small>
      </div>

    </body>
    </html>
  `;

  printWindow.document.write(content);
  printWindow.document.close();
};